import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Mail, Lock, User } from "lucide-react";

export default function AuthModal({ onClose, onSuccess }) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await signIn(email, password);
      } else {
        await signUp(email, password, fullName);
      }
      onSuccess?.();
    } catch (err) {
      setError(err.message === "Invalid login credentials"
        ? "Email ou mot de passe incorrect."
        : err.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="auth-modal"
        className="w-full max-w-md bg-[#0B1F3B] border border-white/10 rounded-2xl p-8 relative"
      >
        <button
          data-testid="auth-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white text-xl"
        >
          &times;
        </button>

        <h2 className="font-heading text-2xl font-semibold text-white mb-2">
          {mode === "login" ? "Connexion" : "Créer un compte"}
        </h2>
        <p className="text-slate-400 text-sm mb-6">
          {mode === "login"
            ? "Connectez-vous pour accéder à vos résultats."
            : "Créez votre compte pour recevoir votre diagnostic."}
        </p>

        {error && (
          <div data-testid="auth-error" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Nom complet</Label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  data-testid="auth-fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="pl-10 bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] h-11"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Email</Label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="pl-10 bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] h-11"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Mot de passe</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                minLength={6}
                className="pl-10 bg-[#0F2545] border-slate-700 text-white placeholder:text-slate-500 focus:border-[#59C3A6] h-11"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="w-full bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold py-3 h-auto rounded-full"
          >
            {loading ? (
              <><Loader2 className="animate-spin mr-2" size={18} /> Chargement...</>
            ) : mode === "login" ? "Se connecter" : "Créer mon compte"}
          </Button>
        </form>

        <div className="mt-5 text-center">
          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
            className="text-[#59C3A6] text-sm hover:underline"
          >
            {mode === "login" ? "Pas encore de compte ? Créer un compte" : "Déjà un compte ? Se connecter"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
